import invisalignLogo from '../assets/icons/align-logo.svg';
import sparkLogo from '../assets/icons/spark-logo.svg';

export const SUPPORT_PHONE = {
    display: '+1 844-386-5483',
    tel: '+18443865483'
} as const;

export const REQUIRED_ACTION_APPROVE_CASE = 'Approve case';

export const ACTION_STYLES: Record<string, { border: string; text: string }> = {
    'Submit case': { border: 'border-[#3773A8]', text: 'text-[#3773A8]' },
    'Approve case': { border: 'border-red-800', text: 'text-red-800' },
    'Start monitoring': { border: 'border-[#007E87]', text: 'text-[#007E87]' },
    'Upload records': { border: 'border-green-500', text: 'text-green-500' },
    'Respond to question': { border: 'border-amber-500', text: 'text-amber-600' },
    'Review for refinement': { border: 'border-[#8B4513]', text: 'text-[#8B4513]' },
    'Upload refinement records': { border: 'border-[#B8860B]', text: 'text-[#B8860B]' },
    'Review for retention': { border: 'border-[#7C3AED]', text: 'text-[#7C3AED]' },
    'Upload retention records': { border: 'border-[#6B21A8]', text: 'text-[#6B21A8]' },
    'No action needed': { border: 'border-gray-300', text: 'text-gray-500' },
    'Deliver aligners': { border: 'border-cyan-700', text: 'text-cyan-700' },
    'Deliver refinement aligners': { border: 'border-[#92400E]', text: 'text-[#92400E]' },
    'Deliver retainers': { border: 'border-[#6D28D9]', text: 'text-[#6D28D9]' },
    'Review design': { border: 'border-[#0F766E]', text: 'text-[#0F766E]' }
};

// Grayed out in the patients table, counted by the "Inactive" stat card, and
// hidden from the default roster.
//
// These are EXTERNAL status labels: the list payload maps `treatmentStatus`
// from `caseStatus` (services/patient.ts). Adding a terminal status to the
// backend means adding its external label here too - it is not derived, and
// missing it silently counts a closed case as active work.
export const INACTIVE_STATUSES = new Set([
    'complete',
    'patient declined tx',
    'patient declined treatment',
    'archived',
    'patient design expired'
]);

export const isInactiveStatus = (status: string | null | undefined): boolean => {
    return INACTIVE_STATUSES.has((status || '').toLowerCase());
};

export const INTERNAL_STATUS_PENDING_APPROVAL = 'Init - 8. Pending approval';

export const INTERNAL_STATUS_PENDING_REFINEMENT_APPROVAL = 'Ref - 6.1. Pending refinement approval';

/**
 * Hold-2 ("in-office step needed"): the ONLY internal status the backend
 * links an in-office-hold-note timeline event to an appointment's
 * `originEvent` for (see appointment.service.ts's `readOriginHoldEvent`
 * guard). Used client-side to gate the NEW-appointment ODO context card's
 * live fetch (see useScheduleAppointmentContext.ts / Composer.tsx): the
 * in-office-hold-note event itself is never deleted once the hold resolves,
 * so without this gate a resolved patient's stale note would keep showing
 * as if it were still live.
 *
 * The external `requiredAction` label 'Schedule appointment' is NOT a safe
 * proxy for this status - other statuses have used the same prompt without
 * an origin-event link - so the internal status id is the only reliable
 * signal.
 *
 * Mirror this literal exactly:
 * backend/src/services/workflow/statuses/internal/hold.ts's
 * `HoldInOfficeStepNeeded`.
 */
export const INTERNAL_STATUS_HOLD_IN_OFFICE = 'Hold - 2. In-office step needed';

export const TREATMENT_STATUS_ON_HOLD = 'On Hold';

export const hasActionRequired = (action: string | null | undefined): action is string => {
    return !!action && action !== 'None';
};

export const isOnHold = (status: string | null | undefined): boolean => {
    return (status || '').toLowerCase() === TREATMENT_STATUS_ON_HOLD.toLowerCase();
};

export const CHAT_QUERY_PARAM = 'chat';
export const CHAT_QUERY_OPEN = 'open';

/**
 * Opens the appointments modal on whatever page renders it (PatientsOverview).
 *
 * Lives here rather than in that page so components inside the page - the
 * patients table's "+" on an empty next-appointment cell - can open the modal
 * without importing the page and creating a cycle. Which appointment is in the
 * composer stays a separate concern owned by AppointmentsModal
 * (APPOINTMENT_QUERY_PARAM / NEW_APPOINTMENT_PARAM /
 * APPOINTMENT_PATIENT_QUERY_PARAM).
 */
export const APPOINTMENTS_QUERY_PARAM = 'appointments';
export const APPOINTMENTS_QUERY_OPEN = 'open';

export const MANUFACTURERS: {
    id: 'Invisalign Aligners' | 'Spark Aligners';
    name: string;
    logo: string;
    className: string;
}[] = [
    {
        id: 'Invisalign Aligners',
        name: 'Invisalign',
        logo: invisalignLogo,
        className: 'h-18 object-contain'
    },
    {
        id: 'Spark Aligners',
        name: 'Spark Aligners',
        logo: sparkLogo,
        className: 'h-12 object-contain'
    }
];
